/**
 * Data access for the Task Manager feature.
 *
 * Talks to the dedicated Task Manager AppSync API (see TaskManagerStack). Every
 * operation from section 5.2 of the TDD takes a single JSON `input` (AWSJSON)
 * and returns JSON (AWSJSON); the resolver Lambda owns shape/validation.
 *
 * The "Include Apps" multiselect is populated from the existing Plus AppSync
 * `getAppsV2` query (the same source the Marketplace uses).
 */

import { callTaskManagerGraphQLDirect, callPlusGraphQLDirect } from "src/utilities/AWSServices";
import { createAuditLog, fetchApps } from "src/Ateam-BI/Templates/Utils/AppActivityUtils";
import {
  createPreference,
  deletePreference,
  getPreference,
  updatePreference,
} from "src/utilities/AppSyncPlusAPIs";

/**
 * Task Manager AppSync ops return an AWSJSON scalar, which the API serializes as
 * a JSON string. Normalize to a JS object/array.
 */
const parseResult = (response) => {
  const dataKey = Object.keys(response.data || {})[0];
  let raw = response.data ? response.data[dataKey] : null;
  if (raw == null) return null;
  // The response VTL runs $util.toJson on a payload the Lambda already
  // json.dumps'd, so the AWSJSON scalar can come back double-encoded. Parse
  // repeatedly until we reach a non-string (object/array), tolerating both
  // single- and double-encoded payloads.
  while (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      break; // Not JSON (e.g. a plain string value) — return as-is.
    }
  }
  return raw;
};

const runTaskManagerOp = async (opName, isMutation, input) => {
  const keyword = isMutation ? "mutation" : "query";
  // The schema declares `input: AWSJSON!` (non-null), so the variable must be
  // declared non-null too and always carry a value (default to an empty object).
  const query = `${keyword} ${opName}($input: AWSJSON!) {
    ${opName}(input: $input)
  }`;
  const response = await callTaskManagerGraphQLDirect(query, {
    input: JSON.stringify(input == null ? {} : input),
  });
  return parseResult(response);
};

/* ---- Section 5.2 operations ---- */

export const listDashboards = (input = {}) => runTaskManagerOp("listDashboards", false, input);
// The dashboard page knows its id, so it fetches just that one dashboard rather
// than pulling the whole list and filtering client-side.
export const getDashboard = (dashboardId) =>
  runTaskManagerOp("getDashboard", false, { dashboard_id: Number(dashboardId) });

// getServices resolves the current period itself. Pass an optional `tracking_group`
// in the input to view a previous cadence instead (see listTrackingGroups).
export const getServices = (input) => runTaskManagerOp("getServices", false, input);

// Distinct tracking periods a dashboard has data for, newest first. Powers the
// "view previous cadence" dropdown; returns { tracking_groups: [str, ...] }.
export const listTrackingGroups = (dashboardId) =>
  runTaskManagerOp("listTrackingGroups", false, { dashboard_id: Number(dashboardId) });

export const getTrackingHistory = (input) => runTaskManagerOp("getTrackingHistory", false, input);

/**
 * For a service, list the DATE columns flagged "required when the service is
 * triggered" across every Task Manager dashboard the service's app belongs to.
 *
 *   idType: "FRISCO" (id = process_id) | "FLEXICO" (id = flex_id) |
 *           "LOGICO" (id = logico_process_id)
 *
 * Returns: [ { dashboard_name, dashboard_id, date_columns: [name, ...] }, ... ]
 */
export const getRequiredDateColumnsByService = (id, idType) =>
  runTaskManagerOp("getRequiredDateColumnsByService", false, {
    id: `${id}`,
    id_type: `${idType}`,
  });

export const createDashboard = (input) => runTaskManagerOp("createDashboard", true, input);

export const updateDashboard = (input) => runTaskManagerOp("updateDashboard", true, input);

// Delete a dashboard and everything attached to its dashboard_id. The RDS
// cascade (dashboard row, services, tracking, custom columns, controls,
// owners/viewers, adhoc tasks) runs server-side; the GenericPreference items
// (column config + status mapping) live in DynamoDB and are cleaned up
// separately via deleteDashboardPreferences.
export const deleteDashboard = (dashboardId) =>
  runTaskManagerOp("deleteDashboard", true, { dashboard_id: Number(dashboardId) });

export const updateProcessLiteViaTaskManager = (input) =>
  runTaskManagerOp("updateProcessLiteViaTaskManager", true, input);

export const updateFlexicoTemplateViaTaskManager = (input) =>
  runTaskManagerOp("updateFlexicoTemplateViaTaskManager", true, input);

export const updateLogicoProcessLiteViaTaskManager = (input) =>
  runTaskManagerOp("updateLogicoProcessLiteViaTaskManager", true, input);
/**
 * Single entry point for all tracking-table writes from the grid. The Lambda
 * dispatches by payload shape:
 *   - A cadence-tracked custom-column value (payload carries `attribute`) is
 *     written to task_manager.tracking_custom_attribute.
 *   - A Status-column edit (payload carries `status`) is written to
 *     task_manager.service_tracking.
 *
 * Status write input: { dashboard_id, service_type: FRISCO|FLEXICO|LOGICO,
 *          service_id, tracking_group, status, tracking_sub_group?, file_id? }
 * Cadence attribute write input: { dashboard_id, service_type, app_id,
 *          service_id, attribute: { key, value, previous_value, ... },
 *          tracking_group, before, after }
 */
export const manageTrackingUpdates = (input) =>
  runTaskManagerOp("manageTrackingUpdates", true, input);

/* ---- AdHoc tasks (confirmation-only deliverables) ----
 * Associated with an entity via { entity_type, entity_id } - entity_type is a
 * name in the adhoc_entity_types lookup (DASHBOARD, APP, ...), entity_id is the
 * id within that type (e.g. a dashboard_id when entity_type = DASHBOARD). */

// List every AdHoc task for an entity (entityType name + id within that type).
export const listAdhocTasks = (entityType, entityId) =>
  runTaskManagerOp("listAdhocTasks", false, {
    entity_type: `${entityType}`,
    entity_id: `${entityId}`,
  });

// Fetch a single AdHoc task (with its uploaders/reviewers).
export const getAdhocTask = (taskId) =>
  runTaskManagerOp("getAdhocTask", false, { task_id: `${taskId}` });

// Create an AdHoc task. input: { entity_type, entity_id, task_name, description,
// uploaders: [{alias,type}], reviewers: [{alias,type,column_index}] }.
export const createAdhocTask = (input) => runTaskManagerOp("createAdhocTask", true, input);

// Update an AdHoc task (task_id required); replaces uploaders/reviewers.
export const updateAdhocTask = (input) => runTaskManagerOp("updateAdhocTask", true, input);

// Delete an AdHoc task.
export const deleteAdhocTask = (taskId) =>
  runTaskManagerOp("deleteAdhocTask", true, { task_id: `${taskId}` });

// Confirm ("mark done") an AdHoc task for a quarter / WB interval.
// input: { task_id, entity_type, entity_id, quarter, tracking_type }.
export const confirmAdhocTask = (input) => runTaskManagerOp("confirmAdhocTask", true, input);

/* ---- Custom Task Manager columns via shared GenericPreference API ---- */

const TASK_MANAGER_COLUMN_PREFERENCE_TYPE = "TASK_MANAGER_COLUMNS";
const TASK_MANAGER_COLUMN_LABEL = "COLUMNS";
const TASK_MANAGER_COLUMN_ASSOCIATION_TYPE = "TASK_MANAGER_DASHBOARD_TAB";

const taskManagerColumnPreferenceId = (dashboardId, tabId) =>
  `task_manager#dashboard#${dashboardId}#tab#${tabId}#columns`;

/* ---- Saved grid "view" (column order + visibility), dashboard-wide ----
 * A single shared layout per dashboard (not per user), persisted as a
 * GenericPreference so it survives reloads and is the same for everyone. The
 * information blob is the AG-Grid column-state array. */

const TASK_MANAGER_VIEW_PREFERENCE_TYPE = "TASK_MANAGER_VIEW";
const TASK_MANAGER_VIEW_LABEL = "VIEW";
const TASK_MANAGER_VIEW_ASSOCIATION_TYPE = "TASK_MANAGER_DASHBOARD_ID";

const taskManagerViewPreferenceId = (dashboardId) =>
  `task_manager#dashboard#${dashboardId}#view`;

// Load the dashboard-wide saved column view. Returns { exists, columnState }.
export const getTaskManagerView = (dashboardId) =>
  new Promise((resolve, reject) => {
    const preferenceId = taskManagerViewPreferenceId(dashboardId);
    getPreference(
      preferenceId,
      (response) => {
        const preference = response?.data?.getPreference || null;
        const parsed = parsePreferenceInformation(preference?.information, []);
        resolve({
          exists: !!preference,
          columnState: Array.isArray(parsed) ? parsed : [],
        });
      },
      preferenceFailure(reject)
    );
  });

// Save (create or update) the dashboard-wide column view. `columnState` is the
// AG-Grid getColumnState() array.
export const saveTaskManagerView = ({ dashboardId, columnState, userId, preferenceExists }) =>
  new Promise((resolve, reject) => {
    const preferenceId = taskManagerViewPreferenceId(dashboardId);
    const information = Array.isArray(columnState) ? columnState : [];
    const onSuccess = () => resolve({ preferenceId, exists: true });
    const onFailure = preferenceFailure(reject);

    if (preferenceExists) {
      updatePreference(
        preferenceId,
        TASK_MANAGER_VIEW_LABEL,
        userId,
        information,
        onSuccess,
        onFailure
      );
      return;
    }
    createPreference(
      preferenceId,
      TASK_MANAGER_VIEW_PREFERENCE_TYPE,
      TASK_MANAGER_VIEW_LABEL,
      String(dashboardId),
      TASK_MANAGER_VIEW_ASSOCIATION_TYPE,
      userId,
      information,
      null,
      onSuccess,
      onFailure
    );
  });

const preferenceFailure = (reject) => (response) =>
  reject(new Error(response?.errors?.[0]?.message || "GenericPreference request failed"));

const parsePreferenceInformation = (information, fallback) => {
  let parsed = information;
  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      return fallback;
    }
  }
  return parsed == null ? fallback : parsed;
};
const parsePreferenceColumns = (information) => {
  const parsed = parsePreferenceInformation(information, []);
  if (!Array.isArray(parsed)) return [];

  const seenColumns = [];
  return parsed.reduce((columns, column, index) => {
    const name = typeof column?.name === "string" ? column.name.trim() : "";
    const type = typeof column?.type === "string" ? column.type.toUpperCase() : "";
    const field = typeof column?.field === "string" ? column.field.trim() : "";
    const identity = type === "DEFAULT" ? `default:${field}` : `custom:${name.toLowerCase()}`;
    if (!name || !type || (type === "DEFAULT" && !field) || seenColumns.includes(identity)) {
      return columns;
    }
    seenColumns.push(identity);

    const order = Number(column.order);
    const normalized = {
      name,
      type,
      is_visible: column.is_visible !== false,
      order: Number.isFinite(order) ? order : index,
    };
    if (type === "DEFAULT") {
      normalized.field = field;
    }
    if (typeof column.due_date_type === "string" && column.due_date_type.trim()) {
      normalized.due_date_type = column.due_date_type.trim();
    }
    if (typeof column.tracking_sub_group === "string" && column.tracking_sub_group.trim()) {
      normalized.tracking_sub_group = column.tracking_sub_group.trim();
    }
    if (column.track_by_cadence === true || column.trackByCadence === true) {
      normalized.track_by_cadence = true;
    }
    if (type === "DATE") {
      normalized.required_when_triggered =
        column.required_when_triggered === true || column.requiredWhenTriggered === true;
      normalized.include_in_digest =
        column.include_in_digest === true || column.includeInDigest === true;
    }
    if (type === "DROPDOWN" && Array.isArray(column.values)) {
      const values = [...new Set(
        column.values
          .filter((value) => typeof value === "string")
          .map((value) => value.trim())
          .filter(Boolean)
      )];
      if (values.length > 0) normalized.values = values;
    }
    columns.push(normalized);
    return columns;
  }, []);
};

export const getTaskManagerColumnConfig = (dashboardId, tabId = "default") =>
  new Promise((resolve, reject) => {
    const preferenceId = taskManagerColumnPreferenceId(dashboardId, tabId);
    getPreference(
      preferenceId,
      (response) => {
        const preference = response?.data?.getPreference || null;
        resolve({
          exists: !!preference,
          preferenceId,
          columns: parsePreferenceColumns(preference?.information),
        });
      },
      preferenceFailure(reject)
    );
  });

export const saveTaskManagerColumnConfig = ({
  dashboardId,
  tabId = "default",
  columns,
  userId,
  preferenceExists,
}) =>
  new Promise((resolve, reject) => {
    const preferenceId = taskManagerColumnPreferenceId(dashboardId, tabId);
    const normalizedColumns = parsePreferenceColumns(columns);
    const onSuccess = () =>
      resolve({ preferenceId, columns: normalizedColumns, exists: true });
    const onFailure = preferenceFailure(reject);

    if (preferenceExists) {
      updatePreference(
        preferenceId,
        TASK_MANAGER_COLUMN_LABEL,
        userId,
        normalizedColumns,
        onSuccess,
        onFailure
      );
      return;
    }

    createPreference(
      preferenceId,
      TASK_MANAGER_COLUMN_PREFERENCE_TYPE,
      TASK_MANAGER_COLUMN_LABEL,
      `${dashboardId}#${tabId}`,
      TASK_MANAGER_COLUMN_ASSOCIATION_TYPE,
      userId,
      normalizedColumns,
      null,
      onSuccess,
      onFailure
    );
  });

/* ---- Task Manager control-panel preferences and RDS dashboard controls ---- */

const TASK_MANAGER_DASHBOARD_ASSOCIATION_TYPE = "TASK_MANAGER_DASHBOARD_ID";
const STATUS_MAPPING_PREFERENCE_TYPE = "STATUS_MAPPING";

const statusMappingPreferenceId = (dashboardId) =>
  `task_manager#dashboard#${dashboardId}#status_mapping`;

const getPreferenceItem = (preferenceId) =>
  new Promise((resolve, reject) => {
    getPreference(
      preferenceId,
      (response) => resolve(response?.data?.getPreference || null),
      preferenceFailure(reject)
    );
  });

const deletePreferenceById = (preferenceId) =>
  new Promise((resolve, reject) => {
    deletePreference(preferenceId, null, () => resolve(preferenceId), preferenceFailure(reject));
  });

/**
 * Remove the DynamoDB GenericPreference items attached to a deleted dashboard:
 * its per-tab column configs and its status-mapping. These are NOT in RDS, so
 * the server-side deleteDashboard cascade cannot touch them.
 *
 * The column-config preference id is per tab, but today every read and write goes
 * through DEFAULT_TAB_ID ("default") -- see getTaskManagerColumnConfig /
 * saveTaskManagerColumnConfig in TaskManagerDashboard -- so no preference item
 * exists for any other tab and the default covers every item that can be there.
 * If per-tab column configs are ever saved, this default silently starts
 * orphaning them: callers must then pass the dashboard's real tab ids.
 *
 * Best-effort: a missing preference is a no-op, and failures are collected and
 * returned rather than thrown, so preference cleanup never blocks a dashboard
 * that was already deleted from RDS.
 *
 * @returns {Promise<{ errors: Error[] }>}
 */
export const deleteDashboardPreferences = async (dashboardId, tabIds = ["default"]) => {
  const preferenceIds = [
    statusMappingPreferenceId(dashboardId),
    ...(Array.isArray(tabIds) && tabIds.length > 0 ? tabIds : ["default"]).map((tabId) =>
      taskManagerColumnPreferenceId(dashboardId, tabId)
    ),
  ];
  const results = await Promise.allSettled(preferenceIds.map(deletePreferenceById));
  const errors = results
    .filter((r) => r.status === "rejected")
    .map((r) => (r.reason instanceof Error ? r.reason : new Error(String(r.reason))));
  return { errors };
};

const upsertPreference = ({
  preferenceId,
  exists,
  preferenceType,
  label,
  associationId,
  associationIdType,
  userId,
  information,
}) =>
  new Promise((resolve, reject) => {
    const onSuccess = () => resolve(preferenceId);
    const onFailure = preferenceFailure(reject);
    if (exists) {
      updatePreference(preferenceId, label, userId, information, onSuccess, onFailure);
      return;
    }
    createPreference(
      preferenceId,
      preferenceType,
      label,
      associationId,
      associationIdType,
      userId,
      information,
      null,
      onSuccess,
      onFailure
    );
  });

// Email Mapping (Alias → Email) is served by the shared Non-Amazon aliases APIs
// (see AppSyncPlusAPIs: getNonAmazonAliases / addNonAmazonAlias / deleteNonAmazonAlias
// backed by acc_central.non_amazon_aliases). It is loaded inline by the Control
// Panel modal on tab open and is intentionally NOT part of getTaskManagerSettings.
export const getTaskManagerSettings = async (dashboardId) => {
  const statusPreference = await getPreferenceItem(statusMappingPreferenceId(dashboardId));

  const statusMappings = parsePreferenceInformation(statusPreference?.information, []);

  return {
    statusMappingExists: !!statusPreference,
    statusMappings: Array.isArray(statusMappings) ? statusMappings : [],
  };
};

// Fetch only this dashboard's status-mapping preference. The dashboard grid loads
// status mappings on every page load just to colour status cells, so this stays
// as a narrow helper that skips the rest of the settings payload.
export const getTaskManagerStatusMappings = async (dashboardId) => {
  const statusPreference = await getPreferenceItem(statusMappingPreferenceId(dashboardId));
  const statusMappings = parsePreferenceInformation(statusPreference?.information, []);
  return Array.isArray(statusMappings) ? statusMappings : [];
};

const normalizeTaskManagerSettingsPreferences = (settings) => {
  const statusMappings = (settings?.statusMappings || []).map((mapping) => ({
    source_status: mapping.key.trim(),
    display_status: mapping.value.trim(),
    // The existing dropdown exposes SUCCESS / ERROR / WARNING. Persist that
    // selection as the display color/style value until dedicated colors exist.
    color: mapping.status,
  }));
  return { statusMappings };
};

const createTaskManagerSettingsAudit = ({ dashboardId, userId, before, after }) =>
  new Promise((resolve, reject) => {
    createAuditLog(
      `TASK_MANAGER#${dashboardId}`,
      `SETTINGS#${Date.now()}`,
      "Task Manager settings preference update",
      JSON.stringify(before),
      JSON.stringify(after),
      "UPDATE_SETTINGS_PREFERENCES",
      userId,
      resolve,
      (error) =>
        reject(
          error instanceof Error
            ? error
            : new Error(error?.errors?.[0]?.message || "Audit log request failed")
        )
    );
  });

export const saveTaskManagerSettings = async ({
  dashboardId,
  userId,
  settings,
  existingConfig,
  dashboard,
}) => {
  const activeTabId = settings?.activeTabId || "controls";

  if (activeTabId === "controls") {
    const dashboardName = dashboard?.name?.trim();
    if (!dashboardName) {
      throw new Error("Dashboard information is required to update settings.");
    }

    const cadence = settings?.cadence || null;
    const updatedDashboard = await updateDashboard({
      dashboard_id: Number(dashboardId),
      name: dashboardName,
      description: dashboard?.description || "",
      logo: dashboard?.logo || "",
      app_ids: Array.isArray(dashboard?.app_ids)
        ? dashboard.app_ids
        : Array.isArray(dashboard?.appIds)
          ? dashboard.appIds
          : [],
      owners: Array.isArray(settings?.owners) ? settings.owners : [],
      viewers: Array.isArray(settings?.viewers) ? settings.viewers : [],
      cadence,
      day: cadence === "WORKDAY" ? Number(settings?.day) : null,
      daily_digest: !!settings?.notifications?.dailyDigest,
      notify_duplicate: !!settings?.notifications?.notifyDuplicate,
    });
    if (updatedDashboard?.error) {
      throw new Error(updatedDashboard.error);
    }

    return {
      ...(existingConfig || {}),
      owners: Array.isArray(updatedDashboard?.owners) ? updatedDashboard.owners : [],
      viewers: Array.isArray(updatedDashboard?.viewers) ? updatedDashboard.viewers : [],
      dashboard: updatedDashboard,
    };
  }

  if (activeTabId === "mapping") {
    const { statusMappings } = normalizeTaskManagerSettingsPreferences(settings);
    const previousStatusMappings = Array.isArray(existingConfig?.statusMappings)
      ? existingConfig.statusMappings
      : [];
    const changed =
      JSON.stringify(previousStatusMappings) !== JSON.stringify(statusMappings);

    if (changed) {
      // Write the preference first; only record the audit entry once it succeeds,
      // so a failed write never leaves an audit trail claiming a change that did
      // not persist.
      await upsertPreference({
        preferenceId: statusMappingPreferenceId(dashboardId),
        exists: !!existingConfig?.statusMappingExists,
        preferenceType: STATUS_MAPPING_PREFERENCE_TYPE,
        label: STATUS_MAPPING_PREFERENCE_TYPE,
        associationId: String(dashboardId),
        associationIdType: TASK_MANAGER_DASHBOARD_ASSOCIATION_TYPE,
        userId,
        information: statusMappings,
      });
      await createTaskManagerSettingsAudit({
        dashboardId,
        userId,
        before: { statusMappings: previousStatusMappings },
        after: { statusMappings },
      });
    }

    return {
      ...(existingConfig || {}),
      // Only claim the preference exists if a write actually happened (or it
      // already existed); otherwise a no-op save on a dashboard that never had a
      // mapping would make the next real save try to update a nonexistent one.
      statusMappingExists: changed || !!existingConfig?.statusMappingExists,
      statusMappings,
    };
  }

  // The Email Mapping tab persists inline via the shared Non-Amazon aliases
  // APIs (see AppSyncPlusAPIs), so it never reaches this batched save path.
  throw new Error(`Unsupported control-panel tab: ${activeTabId}`);
};

/* ---- Logo upload / download via Vault ---- */

const VAULT_SERVICE = "TASK_MANAGER";
// Maps browser MIME types -> the extension Vault stores (also its content_type arg).
const LOGO_MIME_TO_EXT = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/svg+xml": "svg",
  "image/gif": "gif",
};

const parseVaultResult = (response, field) => {
  const raw = response?.data ? response.data[field] : null;
  if (raw == null) return null;
  return typeof raw === "string" ? JSON.parse(raw) : raw;
};

/**
 * Upload a logo through Vault and return the stored S3 object key.
 *
 * Flow: ask Vault for a presigned PUT URL (scoped to the generic bucket +
 * task_manager/ prefix, admin-gated) -> PUT the file to S3 -> return objectKey,
 * which the caller persists as the dashboard `logo`. Download/display resolves
 * the key back to a short-lived URL via getLogoViewUrl (also Vault).
 *
 * @param {File} file
 * @returns {Promise<string>} the Vault object key to store as the dashboard logo
 */
export const uploadLogo = async (file) => {
  const ext = LOGO_MIME_TO_EXT[file.type];
  if (!ext) {
    throw new Error("Unsupported logo type. Use PNG, JPG, SVG or GIF.");
  }
  const fileId = (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const filePath = `task_manager/${fileId}.${ext}`;

  const query = `mutation UploadTaskManagerLogo($service: String!, $content_type: String!, $service_params: AWSJSON!) {
    uploadDocumentToVault(service: $service, content_type: $content_type, service_params: $service_params) {
      uploadUrl
      objectKey
    }
  }`;
  const response = await callPlusGraphQLDirect(query, {
    service: VAULT_SERVICE,
    content_type: ext,
    service_params: JSON.stringify({ file_path: filePath, tagging: "" }),
  });
  const result = parseVaultResult(response, "uploadDocumentToVault");
  if (!result || !result.uploadUrl) {
    throw new Error("Could not get upload URL");
  }

  // Vault signs the PUT with ContentType "application/<ext>"; the header must match.
  const putResponse = await fetch(result.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": `application/${ext}` },
    body: file,
  });
  if (!putResponse.ok) {
    throw new Error("Logo upload failed");
  }
  return result.objectKey;
};

/**
 * Resolve a stored logo object key to a short-lived viewable URL via Vault.
 * @param {string} objectKey - the dashboard `logo` value
 * @returns {Promise<string|null>} a presigned GET URL, or null
 */
export const getLogoViewUrl = async (objectKey) => {
  if (!objectKey) return null;
  const ext = objectKey.split(".").pop();
  const query = `query GetTaskManagerLogo($service: String!, $content_type: String!, $service_params: AWSJSON!) {
    getDocumentFromVault(service: $service, content_type: $content_type, service_params: $service_params) {
      fileUrl
    }
  }`;
  const response = await callPlusGraphQLDirect(query, {
    service: VAULT_SERVICE,
    content_type: ext,
    service_params: JSON.stringify({ file_path: objectKey }),
  });
  const result = parseVaultResult(response, "getDocumentFromVault");
  return result ? result.fileUrl : null;
};

export const manageTabs = (input) => runTaskManagerOp("manageTabs", true, input);

export const manageStatusMapping = (input) => runTaskManagerOp("manageStatusMapping", true, input);

export const manageServiceAttributes = (input) => runTaskManagerOp("manageServiceAttributes", true, input);

export const updateNotifyConfig = (input) => runTaskManagerOp("updateNotifyConfig", true, input);

/**
 * Fetch all apps for the "Include Apps" multiselect (id + name only).
 *
 * Delegates to the shared `fetchApps` helper, which already loads apps once and
 * caches them locally — so we reuse that cache instead of hitting an endpoint
 * from the Task Manager feature.
 * @returns {Promise<Array<{ app_id: string, app_name: string }>>}
 */
export const fetchAllApps = async () => {
  const apps = await fetchApps();
  return Array.isArray(apps) ? apps : [];
};
